import {
  QueryClient,
  VueQueryPlugin,
  type VueQueryPluginOptions,
  QueryCache,
  MutationCache,
  type MutationKey,
} from '@tanstack/vue-query'
import { experimental_createQueryPersister, type AsyncStorage } from '@tanstack/query-persist-client-core'
import { broadcastQueryClient } from '@tanstack/query-broadcast-client-experimental'
import {
  get as idbGet,
  set as idbSet,
  del as idbDelete,
  entries as idbEntries,
  createStore as createIdbStore,
  clear as idbClear,
} from 'idb-keyval'
import { useMessagesStore } from '../stores/messagesStore'
import i18n from './i18n'
import { EveError, EveListError } from '@/utils/api'

const idbStore = createIdbStore('tanstack-query', 'query-storage')

// Timeout for IndexedDB operations. IDB can hang indefinitely in certain browser states
// (private browsing, corrupted database or storage pressure). Since the query cache
// is a performance optimization and not critical, we timeout and return a fallback value
// to keep the app responsive rather than blocking on a potentially stuck IDB operation
const IDB_TIMEOUT_MS = 3000

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, fallbackValue: T): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((resolve) => {
      setTimeout(() => {
        console.warn('[idbQueryStorage] Operation timed out after', timeoutMs, 'ms, returning fallback')
        resolve(fallbackValue)
      }, timeoutMs)
    }),
  ])
}

let clearPromise: Promise<void> | null = null

export const idbQueryStorage = {
  getItem: async (key: string) => {
    if (clearPromise != null) {
      await clearPromise
    }
    try {
      return await withTimeout(idbGet(key, idbStore), IDB_TIMEOUT_MS, undefined)
    } catch {
      return undefined
    }
  },
  setItem: async (key: string, value: unknown) => {
    if (clearPromise != null) {
      await clearPromise
    }
    try {
      await withTimeout(idbSet(key, value, idbStore), IDB_TIMEOUT_MS, undefined)
    } catch {
      // Silently fail
    }
  },
  removeItem: async (key: string) => {
    if (clearPromise != null) {
      await clearPromise
    }
    try {
      await withTimeout(idbDelete(key, idbStore), IDB_TIMEOUT_MS, undefined)
    } catch {
      // Silently fail
    }
  },
  entries: async () => await idbEntries<string>(idbStore),
  clear: async () => {
    if (clearPromise != null) {
      await clearPromise
      return
    }
    clearPromise = withTimeout(idbClear(idbStore), IDB_TIMEOUT_MS, undefined).finally(() => {
      clearPromise = null
    })
    await clearPromise
  },
} satisfies AsyncStorage<unknown> & { clear: () => Promise<void> }

// Hardening vs ada-app-ui: this top-level await runs during
// bootstrap — a rejected fetch must never white-screen the customer portal.
// On failure we fall back to a static buster; the only consequence is that the
// persisted cache isn't api-version-busted that session.
let applicationDetails: Record<'application_version' | 'api_version' | 'storage_cleanup_date', string> | undefined =
  undefined
try {
  applicationDetails = await fetch(new URL('application--details/ada_customer_ui', import.meta.env.VITE_APP_API_ROOT), {
    headers: { Accept: 'text/json' },
  }).then((res) => (res.ok ? (res.json() as Promise<typeof applicationDetails>) : undefined))
} catch {
  console.warn('Failed to fetch application--details — using static cache buster')
}

console.log('API version (cache buster):', applicationDetails?.api_version)

// Nothing is persisted in the shell — every customer GET is user/case-specific,
// volatile data. Opt stable config/reference GETs in here
// when/if a migrated view has any. The persister stays wired (buster etc.) so
// opting in is a one-line change.
const persistedPaths: string[] = []

export const persister = experimental_createQueryPersister({
  storage: idbQueryStorage,
  prefix: 'q',
  maxAge: 1000 * 60 * 60 * 24 * 30,
  buster: applicationDetails?.api_version ?? 'static-buster',
  filters: {
    predicate: (query) => {
      return persistedPaths.includes(query.queryKey[1])
    },
  },
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60,
      staleTime: 1000 * 60 * 5,
      persister: persister.persisterFn,
      shallow: true,
    },
  },
  queryCache: new QueryCache({
    onError(error, context) {
      const notify = typeof context.meta?.notifyOnError === 'boolean' ? context.meta.notifyOnError : true
      if (!notify) {
        return
      }

      const messages = useMessagesStore()

      const fallbackMsg =
        typeof context.meta?.errorMessage === 'string'
          ? i18n.global.t(context.meta.errorMessage)
          : i18n.global.t('system.error')

      if (error instanceof EveError || error instanceof EveListError) {
        messages.addMessage({
          text: error.localeMessage != null ? i18n.global.t(error.localeMessage) : fallbackMsg,
          color: 'error',
        })
      } else {
        messages.addMessage({
          text: fallbackMsg,
          color: 'error',
        })
      }
    },
  }),
  mutationCache: new MutationCache({
    async onSuccess(data, variables, onMutateResult, mutation, context) {
      // Callers performing bulk mutations opt out of per-mutation invalidation
      // via `meta.skipAutoInvalidation` and invalidate once at the end.
      if (context.mutationKey != null && context.meta?.skipAutoInvalidation !== true) {
        await invalidateMutationQueries(context.mutationKey, queryClient)
      }

      const notify = typeof context.meta?.notifyOnSuccess === 'boolean' ? context.meta.notifyOnSuccess : true
      if (notify) {
        const messages = useMessagesStore()

        messages.addMessage(
          typeof context.meta?.successMessage === 'string'
            ? i18n.global.t(context.meta.successMessage)
            : i18n.global.t('common.ok')
        )
      }
    },
    onError(error, variables, onMutateResult, mutation, context) {
      const notify = typeof context.meta?.notifyOnError === 'boolean' ? context.meta.notifyOnError : true
      if (notify) {
        const messages = useMessagesStore()
        const fallbackMsg =
          typeof context.meta?.errorMessage === 'string'
            ? i18n.global.t(context.meta.errorMessage)
            : i18n.global.t('system.error')

        if (error instanceof EveError || error instanceof EveListError) {
          messages.addMessage({
            text: error.localeMessage != null ? i18n.global.t(error.localeMessage) : fallbackMsg,
            color: 'error',
          })
        } else {
          messages.addMessage({
            text: fallbackMsg,
            color: 'error',
          })
        }
      }
    },
  }),
})

broadcastQueryClient({
  queryClient,
})

async function invalidateMutationQueries(mutationKey: MutationKey, queryClient: QueryClient) {
  const method = mutationKey[0]
  const path = mutationKey[1]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (path == null) return
  const parts = path.split('/')

  const invalidations: Promise<unknown>[] = []

  // the mutation doesn't resolve as completed until we've run all the hooks,
  // invalidating the exact resource after a delete will trigger a refetch
  // that will fail like 5 times before resolving. And we don't want that.
  // Instead, we modify the cache directly to remove the exact item if it exists
  if (method === 'delete') {
    queryClient.removeQueries({
      // @ts-expect-error Yes sure the path might not exist for the get method
      queryKey: ['get', path],
    })
  } else {
    invalidations.push(
      queryClient.invalidateQueries({
        // @ts-expect-error They don't need to match 1:1, they'll only be invalidated if they do exist after all
        queryKey: ['get', path],
      })
    )
  }

  // In this case we can assume the path is of form /resource/{id} => ['', 'resource', '{id}']
  // In which case we can also invalidate the list endpoint /resource, and the precise resource /resource/{id}
  // because of the startsWith check. Yes this runs the risk of over-invalidating, for example it will invalidate
  // /resource--subtype as well, but that's an acceptable trade-off.
  if (parts.length === 3) {
    invalidations.push(
      queryClient.invalidateQueries({
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        predicate: (query) => query.queryKey[0] === 'get' && query.queryKey[1]?.startsWith(`/${parts[1]}`),
      })
    )
  }

  await Promise.all(invalidations)
}

export { VueQueryPlugin }
export const vueQueryOptions: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: import.meta.env.DEV,
  queryClient,
}
